import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {
    StyledUserPermissionsFormRow,
    StyledUserPermissionsFormRowData,
    StyledUserPermissionsFormRowHeading
} from './style'
import {PermissionKey, UserPermissionsValidationSchema} from '@/features/users/types'
import {authPermissionsFeatureToLabel, permissionKeysMap} from '@/features/auth/utils'
import {AuthUserPermissionsFeature} from '@/features/auth/types'

export const UserPermissionsForm = ({
    isReadOnly,
    isOverwrittenEnabled
}: {
    isReadOnly: boolean
    isOverwrittenEnabled: boolean
}) => {
    const {t} = useTranslation()
    const form = useFormContext<UserPermissionsValidationSchema>()
    const currentPermissions = useWatch({control: form.control, name: 'permissions'})
    const areCheckboxDisabled = !isOverwrittenEnabled || isReadOnly

    return (
        <Flexbox fullWidth direction="column">
            <StyledUserPermissionsFormRow $isHeader>
                <StyledUserPermissionsFormRowHeading>{t('commons:permissions')}</StyledUserPermissionsFormRowHeading>
                <StyledUserPermissionsFormRowHeading $align="center">
                    {t('commons:display')}
                </StyledUserPermissionsFormRowHeading>
                <StyledUserPermissionsFormRowHeading $align="center">
                    {t('commons:edit')}
                </StyledUserPermissionsFormRowHeading>
            </StyledUserPermissionsFormRow>
            {AuthUserPermissionsFeature.options.map(permissionFeature => {
                // Single-key permissions (e.g. sendMail -> can_send_mail) have no
                // separate write flag: render one checkbox, dash in the edit column.
                const hasDistinctWriteKey =
                    (permissionKeysMap[permissionFeature].read as string) !=
                    (permissionKeysMap[permissionFeature].write as string)

                return (
                    <StyledUserPermissionsFormRow key={permissionFeature}>
                        <StyledUserPermissionsFormRowData>
                            {t(authPermissionsFeatureToLabel[permissionFeature], {count: 2})}
                        </StyledUserPermissionsFormRowData>
                        <Flexbox justify="center">
                            <Controller
                                control={form.control}
                                name="permissions"
                                render={({field}) => {
                                    const isChecked = currentPermissions?.includes(
                                        permissionKeysMap[permissionFeature].read
                                    )
                                    return (
                                        <Checkbox
                                            id={permissionKeysMap[permissionFeature].read}
                                            checked={isChecked}
                                            disabled={
                                                areCheckboxDisabled ||
                                                (hasDistinctWriteKey &&
                                                    currentPermissions?.includes(
                                                        permissionKeysMap[permissionFeature].write
                                                    ))
                                            }
                                            onChange={() => {
                                                let newPermissions: PermissionKey[] = currentPermissions ?? []
                                                if (isChecked) {
                                                    newPermissions = newPermissions.filter(
                                                        permission =>
                                                            permission != permissionKeysMap[permissionFeature].read
                                                    )
                                                } else {
                                                    newPermissions.push(permissionKeysMap[permissionFeature].read)
                                                }
                                                field.onChange(newPermissions)
                                            }}
                                        />
                                    )
                                }}
                            />
                        </Flexbox>
                        <Flexbox justify="center">
                            {hasDistinctWriteKey ? (
                                <Controller
                                    control={form.control}
                                    name="permissions"
                                    render={({field}) => {
                                        const isChecked = currentPermissions?.includes(
                                            permissionKeysMap[permissionFeature].write
                                        )
                                        return (
                                            <Checkbox
                                                id={permissionKeysMap[permissionFeature].write}
                                                checked={isChecked}
                                                disabled={areCheckboxDisabled}
                                                onChange={() => {
                                                    let newPermissions: PermissionKey[] = currentPermissions ?? []
                                                    if (isChecked) {
                                                        newPermissions = newPermissions.filter(
                                                            permission =>
                                                                permission != permissionKeysMap[permissionFeature].write
                                                        )
                                                    } else {
                                                        newPermissions.push(permissionKeysMap[permissionFeature].write)
                                                        if (
                                                            !newPermissions.includes(
                                                                permissionKeysMap[permissionFeature].read
                                                            )
                                                        ) {
                                                            newPermissions.push(
                                                                permissionKeysMap[permissionFeature].read
                                                            )
                                                        }
                                                    }
                                                    field.onChange(newPermissions)
                                                }}
                                            />
                                        )
                                    }}
                                />
                            ) : (
                                <StyledUserPermissionsFormRowData>—</StyledUserPermissionsFormRowData>
                            )}
                        </Flexbox>
                    </StyledUserPermissionsFormRow>
                )
            })}
        </Flexbox>
    )
}
